import { useCallback, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextArea from 'components/Common/Form/Input/TextArea'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { trackClientEvent } from 'analytics/analytics'

interface Props {
  hotelCity: string
  freeText: string
  onFreeTextChange: (text: string) => void
  onGenerate: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripFreeTextSelection({
  hotelCity,
  freeText,
  onFreeTextChange,
  onGenerate,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleFreeTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    onFreeTextChange(newValue)
  }, [onFreeTextChange])

  useEffect(() => {
    trackClientEvent({
      subject: 'final_note',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const stepText = intl.formatMessage(
    {
      id: 'ewX_UMLdx4cN8zd8qwrw',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: '7HjKSTUDOEM9s6EH5cUh',
      defaultMessage: 'Final <i>note</i>',
      description: 'Heading text with italicized note in the trip planner setup wizard',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const questionText = intl.formatMessage(
    {
      id: 'c2Va-P-kHC7z80gZlRaa',
      defaultMessage: 'Anything else we should know about your {hotelCity} trip?',
      description: 'Question asking for additional trip information in the trip planner setup wizard',
    },
    { hotelCity },
  )

  const notesLabel = intl.formatMessage(
    {
      id: 'OdQBQEVNtmur2Iz7hb1e',
      defaultMessage: '<strong>Notes</strong> (optional)',
      description: 'Label for notes input field with optional indicator',
    },
    {
      strong: (chunks: React.ReactNode) => <strong key="bold">{chunks}</strong>,
    },
  )

  const placeholderText = intl.formatMessage({
    id: '2bwIvD6pFrmhTbzmC2Od',
    defaultMessage: 'e.g. Need wheelchair access, Travelling with toddler, Bringing a service dog',
    description: 'Placeholder text for notes textarea with examples in the trip planner setup wizard',
  })

  const createItineraryText = intl.formatMessage({
    id: 'N8yvQGBhATTza7S6lgAy',
    defaultMessage: 'Create my itinerary',
    description: 'Button text to create itinerary on the itinerary or schedule view',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {questionText}
        </BodyText>
      </VerticalSpacer>

      <Group direction="vertical" gap={12} desktopGap={24}>
        <BodyText variant="large">
          {notesLabel}
        </BodyText>
        <TextArea
          value={freeText}
          onChange={handleFreeTextChange}
          placeholder={placeholderText}
          rows={3}
        />
      </Group>
      <div>
        <TextButton
          kind="primary"
          size="large"
          startIcon={<SolidSparklesIcon />}
          onClick={onGenerate}
          disabled={isGeneratingTrip}
        >
          {createItineraryText}
        </TextButton>
      </div>
    </Group>
  )
}

export default AITripFreeTextSelection
